import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { PRODUCT_DATA } from '../services/queries.js';
import { setAuthenticatedUser, clearAuthenticatedUser } from '../services/appInsights';

// Combined GraphQL query for ME, Outlooks, and Config
const INIT_QUERY = gql`query Init {
  me {
    id
    msId
    email
    firstName
    lastName
    companies {
      id
      name
      industry
    }
    outlooks
    dataAccess
    publicationAccess
  }
  config {
    appName
    version
    outlooks
  }
  outlooks {
    id
    title
    shortDescription
    description
    outlookUrl
    overviewUrl
    icon
    hasPermission
    defaultFilters
    restrictFilters
    subTitle
    details
    selectorTitle
    publicationsTag
    publicationTags
    cards {
      icon
      name
    }
    allowed
  }
}`;

const AppDataContext = createContext();

export const useAppData = () => useContext(AppDataContext);

export const AppDataProvider = ({ children }) => {
  const { data, loading, error } = useQuery(INIT_QUERY, { fetchPolicy: 'cache-and-network' });
  console.log('AppDataProvider: query data=', data, 'loading=', loading, 'error=', error);
  const me = data?.me || null;
  const config = data?.config || [];
  const outlooks = data?.outlooks || [];

  console.log('AppDataProvider: me=', me, 'config=', config, 'outlooks=', outlooks);

  // Warm the Azure Function App right after login. The data API runs on a
  // Consumption plan that scales to zero, so the first ProductData query after
  // an idle period pays a cold-start penalty. We fire one lightweight,
  // fire-and-forget query (limit 1, empty filters → the API defaults to the
  // user's allowed products with no activity/country filter, so it reaches
  // Cosmos) as soon as the user and their outlooks are loaded. Runs once per
  // app load; `no-cache` so it never pollutes the real outlook query cache.
  const client = useApolloClient();
  const warmedRef = useRef(false);
  useEffect(() => {
    if (warmedRef.current || !me?.id) return;
    const warmOutlookId = outlooks.find(o => o.hasPermission)?.id || outlooks[0]?.id;
    if (!warmOutlookId) return;
    warmedRef.current = true;
    client
      .query({
        query: PRODUCT_DATA,
        variables: {
          outlookId: warmOutlookId,
          product: [],
          country: [],
          subregion: [],
          activity: [],
          limit: 1,
          continuationToken: null,
        },
        fetchPolicy: 'no-cache',
      })
      .catch(() => { /* warm-up only — ignore failures */ });
  }, [me?.id, outlooks, client]);

  useEffect(() => {
    if (me?.id && me?.companies?.length > 0) {
      setAuthenticatedUser(me.email, me.companies[0].name, {
        customerName: me.companies[0].name,
        userEmail: me.email,
        userName: `${me.firstName} ${me.lastName}`,
      });
    }
    return () => {
      clearAuthenticatedUser();
    };
  }, [me?.id, me?.companies]);

  return (
    <AppDataContext.Provider value={{ me, outlooks, config, loading, error }}>
      {children}
    </AppDataContext.Provider>
  );
};
